import { PaymentMethod } from '../public/store/types/PaymentMethod';
import { ClientStore } from '../stores/stores';

export const PAYMENT_FAILED_REDIRECT_COUNTDOWN_SECONDS = 30;

/**
 * Returns true when a Viva `providerPaymentType` represents the "soft terminal"
 * (Viva Wallet app redirect) flow. The legacy value `PHYSICAL` and the renamed
 * value `SOFT_TERMINAL` (introduced alongside the real Viva physical / Cloud
 * terminal) both map to the same app-redirect behaviour. Centralised here so the
 * several customer checkout entry points share one source of truth and cannot
 * drift apart on a future rename — which is exactly what produced the
 * "OrdersOrderCodeNotFound" regression when one component was missed.
 *
 * Note: the real Viva physical terminal uses `PHYSICAL_TERMINAL` and must NOT be
 * matched here.
 */
export const isVivaSoftTerminalPaymentType = (
  providerPaymentType: string | null | undefined,
): boolean =>
  providerPaymentType === 'PHYSICAL' || providerPaymentType === 'SOFT_TERMINAL';

/**
 * Returns true when a Viva `providerPaymentType` represents the Cloud Terminal
 * (physical hardware terminal) flow. This type has no paymentServerUrl; the
 * backend triggers the terminal directly via the Cloud Terminal API and the
 * frontend polls via the standalone-payment-progress page. Kept separate from
 * isVivaSoftTerminalPaymentType because the two flows differ in how the backend
 * initiates and the frontend polls payment status.
 */
export const isVivaPhysicalTerminalPaymentType = (
  providerPaymentType: string | null | undefined,
): boolean => providerPaymentType === 'PHYSICAL_TERMINAL';

export const isAnyPaymentMethodEnabled = (store: ClientStore): boolean =>
  !!store?.activePaymentMethods?.length;

export const isPaymentMethodEnabled = (
  store: ClientStore,
  paymentMethod: PaymentMethod,
): boolean => !!store?.activePaymentMethods?.includes(paymentMethod);

export interface PaymentFailedRedirectCountdownHandle {
  stop(): void;
}

export const getCountdownBackground = (
  remaining: number,
  totalSeconds: number = PAYMENT_FAILED_REDIRECT_COUNTDOWN_SECONDS,
): string => {
  const pct = (remaining / totalSeconds) * 100;
  return `conic-gradient(#e0e0e0 ${100 - pct}%, #1565c0 ${100 - pct}%)`;
};

export const startPaymentFailedRedirectCountdown = (
  onTick: (remaining: number) => void,
  onExpire: () => void,
  totalSeconds: number = PAYMENT_FAILED_REDIRECT_COUNTDOWN_SECONDS,
): PaymentFailedRedirectCountdownHandle => {
  let remaining = totalSeconds;
  let intervalId: ReturnType<typeof setInterval> | null = null;

  const stop = (): void => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  onTick(remaining);
  intervalId = setInterval(() => {
    remaining--;
    onTick(remaining);
    if (remaining <= 0) {
      stop();
      onExpire();
    }
  }, 1000);

  return { stop };
};
